import React, {
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { FBXLoader } from "three/addons/loaders/FBXLoader.js";
import { VRMLoaderPlugin, VRMUtils } from "@pixiv/three-vrm";
import { mixamoVRMRigMap } from "./MixamoRigMap";
import { call } from "three/webgpu";

const ThreeViewer = forwardRef(
  ({ loadState, defaultAnimation = null, url = null }, ref) => {
    const mountRef = useRef(null);
    const vrmRef = useRef(null);
    const glbRef = useRef(null);
    const mixerRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const controlsRef = useRef(null);
    const gridRef = useRef(null);

    const initialHipsY = useRef(0);

    const recordingRef = useRef(null);
    const recordingCallbackRef = useRef(null);
    const recordingCallbackEndRef = useRef(null);
    const recordingActionRef = useRef(null);

    useImperativeHandle(ref, () => ({
      // initThree,
      loadModel,
      playAnimation,
      recordAnimation,
      stopRecording,
      // recordingCallback,
    }));

    useEffect(() => {
      initThree();
      if(url !== null) loadModel(url);
    }, []);

    function initThree() {
      //Renderer
      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.outputColorSpace = "srgb";
      renderer.setSize(
        mountRef.current.clientWidth,
        mountRef.current.clientHeight
      );
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;

      mountRef.current.appendChild(renderer.domElement);
      //-----------------------------------------------------------------

      //Camera
      const camera = new THREE.PerspectiveCamera(
        30.0,
        mountRef.current.clientWidth / mountRef.current.clientHeight,
        0.01,
        20.0
      );
      camera.position.set(0.0, 1.0, 5.0);
      cameraRef.current = camera;
      //-----------------------------------------------------------------

      //Scene
      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x333439);
      sceneRef.current = scene;
      //-----------------------------------------------------------------

      //Lights
      const light = new THREE.DirectionalLight(0xffffff, 1);
      light.position.set(5, 10, 7.5);
      light.castShadow = true;
      sceneRef.current.add(light);

      const ambientLight = new THREE.AmbientLight(0xffffff, 3);
      sceneRef.current.add(ambientLight);
      //-----------------------------------------------------------------

      //Orbit Controls
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.screenSpacePanning = false;
      controls.maxPolarAngle = Math.PI / 2;
      controls.minDistance = 0.5;
      controls.maxDistance = 10;
      controlsRef.current = controls;
      //-----------------------------------------------------------------

      //Grid
      const size = 5;
      const divisions = 10;

      const gridHelper = new THREE.GridHelper(size, divisions);
      gridHelper.position.set(0, 0, 0);
      scene.add(gridHelper);
      gridRef.current = gridHelper;

      const planeGeometry = new THREE.PlaneGeometry(5, 5);
      const planeMaterial = new THREE.ShadowMaterial({ opacity: 0.5 });
      const plane = new THREE.Mesh(planeGeometry, planeMaterial);
      plane.rotation.x = -Math.PI / 2;
      plane.position.set(0, 0, 0);
      plane.receiveShadow = true;
      scene.add(plane);
      //-----------------------------------------------------------------

      //Re-size
      const resizeHandler = () => {
        if (mountRef.current != null) {
          const width = mountRef.current.clientWidth;
          const height = mountRef.current.clientHeight;
          renderer.setSize(width, height);
          camera.aspect = width / height;
          camera.updateProjectionMatrix();
        } else {
          window.removeEventListener("resize", resizeHandler);
        }
      };
      window.addEventListener("resize", resizeHandler);
      //-----------------------------------------------------------------

      // Update loop
      const clock = new THREE.Clock();
      let lastFrame = -1;
      const update = () => {
        requestAnimationFrame(update);
        const delta = clock.getDelta();

        if (mixerRef.current) mixerRef.current.update(delta);

        if (vrmRef.current && vrmRef.current.update) {
          vrmRef.current.update();
        }

        if (glbRef.current) {
          glbRef.current.rotation.y += 0.01;
        }

        renderer.render(scene, camera);

        // recording
        if(recordingRef.current){
          const action = recordingActionRef.current;
          const duration = action.getClip().duration; // Total duration of the animation
          const fps = 30; // Assume 30 frames per second
          const currentTime = action.time; // Current time in the animation
          const totalFrames = Math.floor(duration * fps); // Total frames in the animation
          const currentFrame = Math.floor((currentTime / duration) * totalFrames) % totalFrames;

          if(currentFrame !== lastFrame && currentFrame % 3 === 0){
            const dataURL = renderer.domElement.toDataURL("image/png");
            if(recordingCallbackRef.current) recordingCallbackRef.current(currentFrame, dataURL);
          }
          if(currentFrame === totalFrames-1){
            // recording end
            recordingCallbackEndRef.current();
            stopRecording();
          } 
          lastFrame = currentFrame;
        }
      };
      update();
      //-----------------------------------------------------------------
    }

    //Borro todo lo de la escena para cambiar el glb por el vrm
    const clearScene = () => {
      if (vrmRef.current) {
        sceneRef.current.remove(vrmRef.current.scene);
        vrmRef.current = null;
      } else if (glbRef.current) {
        sceneRef.current.remove(glbRef.current);
        glbRef.current = null;
      }
    };

    const recordAnimation = async (model, animation, callback, callback_end) => {
      console.log("recording", animation)
      const clip = await playAnimation(animation);
      recordingActionRef.current = clip;
      recordingCallbackRef.current = callback;
      recordingCallbackEndRef.current = callback_end;
      recordingRef.current = true;
      gridRef.current.visible = false;
    }
    
    const stopRecording = () => {
      recordingRef.current = null;
      recordingCallbackRef.current = null;
      recordingCallbackEndRef.current = null;
      gridRef.current.visible = true;
    }

    const loadModel = (url) => {
      clearScene();

      console.log("loading model", url);

      const extension = url.split(".").pop().toLowerCase();

      if (extension === "vrm") {
        loadVRM(url);
      } else if (extension === "glb" || extension === "gltf") {
        loadGLB(url);
      } else if (extension === "fbx") {
        loadFBX(url);
      } else {
        console.error("Unsupported file format!");
      }
    };

    const loadVRM = (url) => {
      loadState(true);
      const loader = new GLTFLoader();
      loader.register((parser) => new VRMLoaderPlugin(parser));

      loader.load(url, (gltf) => {
        const vrm = gltf.userData.vrm;
        VRMUtils.rotateVRM0(vrm);
        sceneRef.current.add(vrm.scene);

        vrmRef.current = vrm;
        mixerRef.current = new THREE.AnimationMixer(vrm.scene);
        fitCameraToObject(cameraRef.current, vrm.scene, controlsRef.current);

        vrm.scene.traverse((node) => {
          if (node.isMesh) {
            node.castShadow = true; // Habilita las sombras
            // if(typeof(node.material) == Array){
            //   node.material.forEach((material) => {
            //     material.roughness = 0.5;
            //     material.metalness = 0.0;
            //   })
            // }else if(node.material){
            //   node.material.roughness = 0.5;
            //   node.material.metalness = 0.0;
            // }
          }
        });


        const hipsNode = vrm.humanoid?.getNormalizedBoneNode("hips");
        initialHipsY.current = hipsNode.position.y;

        loadState(false);

        if (defaultAnimation) playAnimation(defaultAnimation);
      });
    };

    const loadGLB = (url) => {
      loadState(true);
      const loader = new GLTFLoader();
      loader.load(url, (gltf) => {
        sceneRef.current.add(gltf.scene);
        glbRef.current = gltf.scene;
        fitCameraToObject(cameraRef.current, gltf.scene, controlsRef.current);
        loadState(false);
      });
    };

    


    const loadFBX = (url) => {
      function createStandardMaterial(oldMat) {
        const matParams = {
          color: oldMat.color ? oldMat.color.clone() : new THREE.Color(0xffffff),
          map: oldMat.map || null,
          metalness: oldMat.metalness !== undefined ? oldMat.metalness : 0.5,
          roughness: oldMat.roughness !== undefined ? oldMat.roughness : 0.5,
          emissive: oldMat.emissive ? oldMat.emissive.clone() : new THREE.Color(0x000000),
          emissiveMap: oldMat.emissiveMap || null,
          normalMap: oldMat.normalMap || null,
          roughnessMap: oldMat.roughnessMap || null,
          metalnessMap: oldMat.metalnessMap || null,
          side: oldMat.side,
          transparent: oldMat.transparent,
          opacity: oldMat.opacity,
        };
        return new THREE.MeshStandardMaterial(matParams);
      }
      loadState(true);
      const loader = new FBXLoader();
      loader.load(url, (obj) => {

        obj.traverse((child) => {
          if (child.isMesh) {
            console.log(child.material.map); // is it undefined?
            console.log(child.material);     // what properties exist?

            // console.log('Mesh:', child.name);
            // console.log('Material:', child.material);
            // if (child.material.map) {
            //   console.log('Diffuse map found:', child.material.map);
            // } else {
            //   console.warn('No texture map found for:', child.name);
            // }
            // const oldMat = child.material;

            // // Handle multi-materials
            // if (Array.isArray(oldMat)) {
            //   child.material = oldMat.map((mat) => createStandardMaterial(mat));
            // } else {
            //   child.material = createStandardMaterial(oldMat);
            // }

            // child.material.needsUpdate = true;
            // child.castShadow = true;
            // child.receiveShadow = true;
          }
        });
        
        sceneRef.current.add(obj);
        glbRef.current = obj;
        fitCameraToObject(cameraRef.current, obj, controlsRef.current);
        loadState(false);
      });
    };

    async function playAnimation(url) {
      console.log("play animation", url);

      if (vrmRef.current) {
        const clip = await loadMixamoAnimation(url, vrmRef.current);
        // console.log(mixerRef.current);

        mixerRef.current.stopAllAction();
        const action = mixerRef.current.clipAction(clip).reset()
        action.play();
        return action;
      }
      return null;
    }

    function fitCameraToObject(camera, object, controls) {
      const boundingBox = new THREE.Box3().setFromObject(object);
      const center = boundingBox.getCenter(new THREE.Vector3());
      const size = boundingBox.getSize(new THREE.Vector3());

      const fov = camera.fov * (Math.PI / 180);
      const aspectRatio = camera.aspect;

      let distance = size.y / 2 / Math.tan(fov / 2);
      if (aspectRatio < size.x / size.y) {
        distance = size.x / 2 / (Math.tan(fov / 2) * aspectRatio);
      }

      camera.position.set(center.x, center.y, center.z + distance);
      camera.near = distance / 100;
      camera.far = distance * 10;
      camera.updateProjectionMatrix();

      controls.target.copy(center);
      controls.update();
    }

    function loadMixamoAnimation(url, vrm) {
      const loader = new FBXLoader(); // A loader which loads FBX

      return loader.loadAsync(url).then((asset) => {
        const clip = THREE.AnimationClip.findByName(
          asset.animations,
          "mixamo.com"
        ); // extract the AnimationClip

        function resetBoneTransformations(vrm) {
          const hipsNode = vrm.humanoid?.getNormalizedBoneNode("hips");
          if (hipsNode) {
            hipsNode.position.set(0, initialHipsY.current, 0);
          }
        }

        // Llama a esta función al inicio de tu carga de animaciones
        resetBoneTransformations(vrm);

        const tracks = []; // KeyframeTracks compatible with VRM will be added here

        const restRotationInverse = new THREE.Quaternion();
        const parentRestWorldRotation = new THREE.Quaternion();
        const _quatA = new THREE.Quaternion();
        const _vec3 = new THREE.Vector3();

        // Adjust with reference to hips height.
        const motionHipsHeight =
          asset.getObjectByName("mixamorigHips").position.y;
        const vrmHipsY = vrm.humanoid
          ?.getNormalizedBoneNode("hips")
          .getWorldPosition(_vec3).y;
        const vrmRootY = vrm.scene.getWorldPosition(_vec3).y;
        const vrmHipsHeight = Math.abs(vrmHipsY - vrmRootY);
        const hipsPositionScale = vrmHipsHeight / motionHipsHeight;

        clip.tracks.forEach((track) => {
          // Convert each tracks for VRM use, and push to `tracks`
          const trackSplitted = track.name.split(".");
          const mixamoRigName = trackSplitted[0];
          const vrmBoneName = mixamoVRMRigMap[mixamoRigName];
          const vrmNodeName =
            vrm.humanoid?.getNormalizedBoneNode(vrmBoneName)?.name;
          const mixamoRigNode = asset.getObjectByName(mixamoRigName);

          if (vrmNodeName != null) {
            const propertyName = trackSplitted[1];

            // Store rotations of rest-pose.
            mixamoRigNode.getWorldQuaternion(restRotationInverse).invert();
            mixamoRigNode.parent.getWorldQuaternion(parentRestWorldRotation);

            if (track instanceof THREE.QuaternionKeyframeTrack) {
              // Retarget rotation of mixamoRig to NormalizedBone.
              for (let i = 0; i < track.values.length; i += 4) {
                const flatQuaternion = track.values.slice(i, i + 4);

                _quatA.fromArray(flatQuaternion);

                _quatA
                  .premultiply(parentRestWorldRotation)
                  .multiply(restRotationInverse);

                _quatA.toArray(flatQuaternion);

                flatQuaternion.forEach((v, index) => {
                  track.values[index + i] = v;
                });
              }

              tracks.push(
                new THREE.QuaternionKeyframeTrack(
                  `${vrmNodeName}.${propertyName}`,
                  track.times,
                  track.values.map((v, i) =>
                    vrm.meta?.metaVersion === "0" && i % 2 === 0 ? -v : v
                  )
                )
              );
            } else if (track instanceof THREE.VectorKeyframeTrack) {
              const value = track.values.map(
                (v, i) =>
                  (vrm.meta?.metaVersion === "0" && i % 3 !== 1 ? -v : v) *
                  hipsPositionScale
              );
              tracks.push(
                new THREE.VectorKeyframeTrack(
                  `${vrmNodeName}.${propertyName}`,
                  track.times,
                  value
                )
              );
            }
          }
        });

        //Esto ignora todo los tracks que modifiquen la posicion
        // const filteredTracks = tracks.filter(
        //   (track) => !track.name.includes(".position")
        // );
        return new THREE.AnimationClip("vrmAnimation", clip.duration, tracks);
      });
    }

    return <div ref={mountRef} style={{ width: "100%", height: "100%" }}></div>;
  }
);

export default ThreeViewer;
